import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../navbar';
import { productsService } from '../../services/api/products.service';
import { config, buildImageUrl } from '../../config';

function ProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    images: []
  });
  const [currentImages, setCurrentImages] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [categoriesData, productData] = await Promise.all([
        productsService.getAllCategories(),
        productsService.getProductById(id)
      ]);
      
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setFormData({
        name: productData.name,
        description: productData.description,
        category: productData.category,
        price: productData.price,
        images: []
      });
      setCurrentImages(productData.images || []);
    } catch (err) {
      setError((err && (err.message || err.error)) || String(err));
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      images: files
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('price', formData.price);
      
      formData.images.forEach(image => {
        formDataToSend.append('images', image);
      });

      await productsService.updateProduct(id, formDataToSend);
      navigate('/products');
    } catch (err) {
      setError((err && (err.message || err.error)) || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex">
        <Navbar />
      </div>
      <div className="max-w-2xl mx-auto p-4 mt-20">
        <h1 className="text-2xl font-bold mb-6">Edit Product</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              required
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Images
            </label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {currentImages.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={buildImageUrl(image)}
                    alt={`Product ${index + 1}`}
                    className="w-full h-24 object-cover rounded"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = config.defaultImage;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload New Images
            </label>
            <input
              type="file"
              name="images"
              onChange={handleImageChange}
              multiple
              accept="image/*"
              className="w-full px-3 py-2 border rounded-md"
            />
            <p className="text-sm text-gray-500 mt-1">
              Leave empty to keep current images
            </p>
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="px-4 py-2 text-gray-700 border rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-white bg-blue-600 rounded-md disabled:bg-blue-400"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default ProductEdit;